import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthController, useRebaseClient } from "@rebasepro/app";
import {
    BackToList,
    useAnnouncedSnackbar,
    useMasterDetail,
    workspaceListCls,
    workspaceRootCls,
} from "./_workspace";
import type { RebaseClient } from "@rebasepro/types";
import { employerKey, isAnonymousCompanyName, preferredEmployerName } from "../employer_identity";
import { offerLink } from "../offer_link";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    DateTimeField as RebaseDateTimeField,
    FilterChip as RebaseFilterChip,
    IconButton as RebaseIconButton,
    SearchBar as RebaseSearchBar,
    Separator as RebaseSeparator,
    TextField as RebaseTextField,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    RefreshCwIcon as RebaseRefreshCwIcon,
    cls,
    cardClickableMixin,
    cardSelectedMixin,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";
import { Globe as RebaseGlobeIcon } from "lucide-react";

import { ProspectContactsPanel, type PanelAsk } from "./ProspectContactsPanel";
import { ProspectActivityPanel } from "./ProspectActivityPanel";
import type { ThreadRow } from "./_email_shared";
import { OwnerControl, useTeam } from "./_team";
import { EntityLink } from "./_links";
import { EntityEmailPanel } from "./EntityEmailPanel";
import {
    INBOUND_COLOR,
    INBOUND_LABEL,
    fetchInbound,
    isUntaken,
    takeInbound,
    type InboundItem
} from "./_prospects_inbound";
import {
    DUE_COLOR,
    EMPLOYER_PARAM,
    INBOUND_PARAM,
    STAGE_ACTION,
    STAGE_COLOR,
    STAGE_LABEL,
    STAGE_ORDER,
    STATUS_COLOR,
    TABS,
    compareContacts,
    contactLabel,
    dueLabel,
    dueState,
    fetchAllRows,
    inDays,
    isInbound,
    isRecentOffer,
    marketFit,
    MARKET_ORDER,
    PROSPECT_TAB_HINT,
    isTodo,
    sinceLabel,
    stageHint,
    stageOf,
    statusLabel,
    type ActivityKind,
    type ActivityRow,
    type ContactRow,
    type Employer,
    type ProspectRow,
    type ProspectSource,
    type ProspectStatus,
    type ScrapedRow,
    type Stage
} from "./_prospects_shared";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const DateTimeField = RebaseDateTimeField as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const SearchBar = RebaseSearchBar as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const RefreshCwIcon = RebaseRefreshCwIcon as React.ComponentType<any>;
const GlobeIcon = RebaseGlobeIcon as React.ComponentType<any>;

/**
 * How many offers this screen reads in one go.
 *
 * The scraper adds offers every weekday and `retire-stale-jobs` expires them
 * rather than deleting them, so the table only grows — this limit will be
 * reached. When it is, the oldest offers fall off and the vacancy counts on this
 * screen quietly become wrong, which is the failure mode that would never be
 * noticed. Hence `dropped` and the banner it feeds: a cap that truncates in
 * silence reads as "these are all the employers", and it is not.
 */
const OFFER_LIMIT = 1000;

/**
 * The two work filters and the six funnel ones — see `visible` below.
 *
 * "todo" and "due" are not statuses: they are derived from `stageOf`, and they
 * lead because the question somebody has when they open this screen is what to
 * do, not what stage a company is at.
 */
type Tab = ProspectStatus | "due" | "todo" | "mine" | "inbound";

/**
 * Where a link asked to land, read once from the address — see `prospectsUrl`.
 *
 * Read on the first render and cleared from the address bar at once, the way
 * the review queue does it with `?oferta=`: the parameter says how the screen
 * was entered, and left in place a reload would drag the reader back to it
 * after they had moved on.
 */
function consumeDeepLink(): { employer: string | null; inbound: string | null } | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const employer = params.get(EMPLOYER_PARAM);
    const inbound = params.get(INBOUND_PARAM);
    if (!employer && !inbound) return null;
    params.delete(EMPLOYER_PARAM);
    params.delete(INBOUND_PARAM);
    const query = params.toString();
    window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
    return { employer, inbound };
}

function offerDate(o: ScrapedRow): string | null {
    return o.posted_at ?? o.scraped_at ?? null;
}

/**
 * Top-level "Empresas que contratan" workspace.
 *
 * The scraper writes a real employer with a real open vacancy into
 * `scraped_jobs` every weekday, and read down the employer axis that is the
 * list of companies in Spain and Latin America hiring for sustainability right
 * now — with their website and the date they last posted. A company with an
 * open vacancy has budget approved and a hiring manager already behind
 * schedule, which is the moment to reach them.
 *
 * ── The four things this screen holds ────────────────────────────────
 *
 * 1. **The employers**, derived live by grouping the queue. Not stored: the
 *    scrape refreshes them daily and at no cost.
 * 2. **The status**, in `company_prospects` — one row per employer, written the
 *    first time somebody acts on one, so an untouched list of 300 costs nothing.
 * 3. **The people**, in `prospect_contacts`. This is what turned the list into
 *    something usable: a prospect with a status and nobody to write to is a row
 *    that has to be researched by hand, and 300 of those is a week nobody has.
 *    "Buscar personas" reads the employer's own offers and public site; every
 *    contact carries where it came from and whether its address was published,
 *    typed, or guessed from a pattern.
 * 4. **The history**, in `prospect_activity`, plus a `next_action_at` that makes
 *    "Para hoy" a real queue instead of a list somebody scrolls.
 *
 * ── It has to say what to do ─────────────────────────────────────────
 *
 * All four of those are *state*, and a screen that shows only state reads as a
 * filing cabinet: thirty-odd companies all labelled "Sin contactar", a detail
 * pane of empty fields, and no answer to "so what do I do". `stageOf` is the
 * answer — one derived instruction per employer — and it drives three things
 * here: the "Por hacer" tab that this screen now opens on, the headings that cut
 * that list into batches of like work, and the "Lo siguiente" card at the top of
 * the detail pane, whose single button performs the step it names.
 *
 * "Escribir" on a contact now sends from the SustenTalent mailbox and files the
 * conversation in "Correo" against this employer, so a reply comes back into the
 * panel instead of into somebody's Gmail. Until drizzle/0042 it was a `mailto:`
 * that could only log that an email had been *started*.
 *
 * What it deliberately does not do is pair employers with candidates. The
 * shortlist for an outreach email is built by a person in the talent search,
 * against the vacancy they are answering, and nothing here writes a link between
 * a vacancy and a person who has not applied to it.
 */
export function ProspectsView() {
    const client = useRebaseClient<RebaseClient>();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();
    const { openDetail, closeDetail, masterCls, detailCls } = useMasterDetail();
    const auth = useAuthController();
    const { me, shortName } = useTeam();

    const [offers, setOffers] = useState<ScrapedRow[] | null>(null);
    const [prospects, setProspects] = useState<ProspectRow[]>([]);
    const [contacts, setContacts] = useState<ContactRow[]>([]);
    const [activity, setActivity] = useState<ActivityRow[]>([]);
    /** Conversations with the selected employer, merged into the log below. */
    const [threads, setThreads] = useState<ThreadRow[]>([]);
    /** Demo requests and contact-form messages — see _prospects_inbound.ts. */
    const [inbound, setInbound] = useState<InboundItem[]>([]);
    const [selectedInboundId, setSelectedInboundId] = useState<string | null>(null);
    const [taking, setTaking] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    /**
     * Opens on the work, always.
     *
     * This used to open on "Sin contactar" and jump to "Para hoy" when something
     * was due — so on every morning with nothing scheduled the first screen was
     * thirty identical rows in the one status that says least about them. "Por
     * hacer" sorts the due work to the top by itself, so the special case that
     * chose between them is gone with it.
     */
    const [tab, setTab] = useState<Tab>("todo");
    /**
     * Decided once, after the first load: "Nos escribieron" when anybody has,
     * "Por hacer" otherwise. An employer who wrote to us is worth many times
     * one we found — on 2026-09-05 one had asked for a recruitment process two
     * days earlier and sat as the third chip while the first tab led with a
     * consultancy in Massachusetts.
     */
    const [tabChosen, setTabChosen] = useState(false);
    // A ref, and `undefined` until read: consumed exactly once, on the first
    // render — see `consumeDeepLink`.
    const pendingDeepLink = useRef<{ employer: string | null; inbound: string | null } | null | undefined>(undefined);
    if (pendingDeepLink.current === undefined) {
        pendingDeepLink.current = consumeDeepLink();
    }
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState<"recent" | "volume">("recent");
    /**
     * Spain and Latin America only, by default. Off is one click and the chip
     * says how many it is hiding, so the rest is never lost — only not first.
     */
    const [marketOnly, setMarketOnly] = useState(true);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [working, setWorking] = useState(false);
    /**
     * A step asked for from the "Lo siguiente" card and performed by the
     * contacts panel, which owns the search, the form and the composer. Bumping
     * `n` is what re-fires it, so pressing the same button twice works.
     */
    const [ask, setAsk] = useState<PanelAsk | null>(null);
    /** Offers that exist but did not fit in `OFFER_LIMIT`. Never silently zero. */
    const [dropped, setDropped] = useState(0);

    // The editable half of the detail pane. Held apart from `prospects` so a
    // half-typed note is not overwritten by a background reload, and reset when
    // the selection moves.
    const [draft, setDraft] = useState<{ notes: string; next_action: string; next_action_at: Date | null }>({
        notes: "", next_action: "", next_action_at: null
    });

    const data = (client as any)?.data;

    const load = useCallback(async () => {
        if (!data) return;
        setLoading(true);
        setError("");
        try {
            const [offerRes, prospectRows, contactRows, inboundRows] = await Promise.all([
                data.collection("scraped-jobs").find({ limit: OFFER_LIMIT, orderBy: ["scraped_at", "desc"] }),
                fetchAllRows<ProspectRow>(data, "company-prospects"),
                fetchAllRows<ContactRow>(data, "prospect-contacts"),
                // Never fails the screen: the inbound queue is one tab, and an
                // employer list that refuses to load because a contact form
                // errored would be the worse outcome by far.
                fetchInbound(data).catch(() => [] as InboundItem[])
            ]);
            const rows = offerRes?.data ?? [];
            setOffers(rows);
            // `meta.total` is the count before the limit, so this is the number
            // of offers that exist and are not on this screen.
            setDropped(Math.max(0, (offerRes?.meta?.total ?? rows.length) - rows.length));
            setProspects(prospectRows);
            setContacts(contactRows);
            setInbound(inboundRows);
        } catch (err: any) {
            setError(err?.message || "No se pudieron cargar las empresas");
        } finally {
            setLoading(false);
        }
    }, [data]);

    useEffect(() => {
        void load();
    }, [load]);

    /**
     * The log for one employer, read on selection rather than all at once.
     *
     * Unlike prospects and contacts this table only grows — one row per act,
     * forever — so reading it whole would get slower every week for a panel that
     * shows one employer at a time.
     */
    const loadActivity = useCallback(async (key: string | null, companyId?: string | null) => {
        if (!data || !key) {
            setActivity([]);
            setThreads([]);
            return;
        }
        try {
            // The conversations come along for the ride, and they are what let
            // the log say they *answered*. Filed under the prospect key, and —
            // for an employer who also has a brand record — under that record's
            // id, because mail sent from the company screen is filed there.
            // Two shapes of the same employer, which is what 0044 exists to
            // reconcile; until every old thread is relabelled, read both.
            const queries: Promise<any>[] = [
                data.collection("prospect-activity").find({
                    where: { employer_key: ["==", key] },
                    orderBy: ["created_at", "desc"],
                    limit: 100
                }),
                data.collection("email-threads").find({
                    where: { employer_key: ["==", key] },
                    orderBy: ["last_message_at", "desc"],
                    limit: 50
                })
            ];
            if (companyId) {
                queries.push(data.collection("email-threads").find({
                    where: { entity_type: ["==", "company"], entity_id: ["==", companyId] },
                    orderBy: ["last_message_at", "desc"],
                    limit: 50
                }));
            }
            const [activityRes, ...threadPages] = await Promise.all(queries);
            setActivity(activityRes?.data ?? []);
            const seen = new Map<string, ThreadRow>();
            for (const page of threadPages) {
                for (const row of ((page?.data ?? []) as ThreadRow[])) {
                    if (row?.id) seen.set(row.id, row);
                }
            }
            setThreads([...seen.values()]);
        } catch {
            // A missing history must not break the rest of the card.
            setActivity([]);
            setThreads([]);
        }
    }, [data]);

    const prospectByKey = useMemo(() => {
        const map = new Map<string, ProspectRow>();
        for (const p of prospects) if (p.employer_key) map.set(p.employer_key, p);
        return map;
    }, [prospects]);

    const contactsByKey = useMemo(() => {
        const map = new Map<string, ContactRow[]>();
        for (const contact of contacts) {
            if (!contact.employer_key) continue;
            const list = map.get(contact.employer_key);
            if (list) list.push(contact);
            else map.set(contact.employer_key, [contact]);
        }
        return map;
    }, [contacts]);

    const employers = useMemo<Employer[]>(() => {
        if (!offers) return [];

        const groups = new Map<string, { names: string[]; open: ScrapedRow[]; past: ScrapedRow[] }>();
        for (const o of offers) {
            // A rejected offer was judged not to be a sustainability vacancy, so
            // it is no evidence about the employer and must not put them on this
            // list. An expired one is: it says they hire for these roles, just
            // not today, which is why it counts separately rather than not at all.
            if (o.status === "rejected") continue;
            if (isAnonymousCompanyName(o.company_name)) continue;

            const key = employerKey(o.company_name);
            if (!key) continue;

            let group = groups.get(key);
            if (!group) {
                group = { names: [], open: [], past: [] };
                groups.set(key, group);
            }
            group.names.push(o.company_name ?? "");
            // Open by *date*, not by review status. The review queue now
            // writes an unreviewed offer off after 14 days (STALE_SCRAPE_DAYS),
            // which is a fact about the board, not about the employer: a
            // posting three weeks old still has a hiring manager behind it.
            // An offer stops being evidence of hiring *now* at six weeks,
            // whatever anybody did or did not do with it in the queue.
            (isRecentOffer(o) ? group.open : group.past).push(o);
        }

        // Every employer who has a record but no scraped offer — the ones who
        // wrote to us, and the ones somebody added by hand. Before 0044 they
        // could not exist; now that they can, leaving the list derived purely
        // from the scrape queue would store them and never show them, which is
        // the worst of the two.
        for (const prospect of prospects) {
            if (prospect.employer_key && !groups.has(prospect.employer_key)) {
                groups.set(prospect.employer_key, {
                    names: [prospect.employer_name ?? prospect.employer_key],
                    open: [],
                    past: []
                });
            }
        }

        return [...groups.entries()].map(([key, group]) => {
            const all = [...group.open, ...group.past];
            const prospect = prospectByKey.get(key) ?? null;
            const employerContacts = contactsByKey.get(key) ?? [];
            const countries = [...new Set(
                all.map(o => o.location_country).filter((c): c is string => !!c)
            )];
            const lastSeen = all
                .map(offerDate)
                .filter((d): d is string => !!d)
                .sort()
                .pop() ?? null;

            return {
                key,
                // The stored name wins once there is one: it is what somebody saw
                // when they decided to contact this employer, and a later offer
                // spelled differently should not rename a company mid-conversation.
                name: prospect?.employer_name || preferredEmployerName(group.names),
                website: prospect?.employer_website
                    ?? all.find(o => o.employer_website)?.employer_website
                    ?? null,
                logo: all.find(o => o.employer_logo)?.employer_logo ?? null,
                open: group.open.sort((a, b) => (offerDate(b) ?? "").localeCompare(offerDate(a) ?? "")),
                past: group.past,
                countries,
                lastSeen,
                prospect,
                status: (prospect?.status ?? "new") as ProspectStatus,
                contacts: employerContacts,
                // "Reachable" is the number that matters on the list: a contact
                // with no address and no profile is a name, not a way in.
                reachable: employerContacts.filter(c => (c.email ?? "").trim() || (c.linkedin_url ?? "").trim()).length,
                companyId: prospect?.company_id ?? null,
                source: (prospect?.source ?? "scraped") as ProspectSource
            };
        });
    }, [offers, prospects, prospectByKey, contactsByKey]);

    const counts = useMemo(() => {
        const c: Record<ProspectStatus, number> = {
            new: 0, contacted: 0, replied: 0, meeting: 0, won: 0, discarded: 0
        };
        for (const e of employers) c[e.status] += 1;
        return c;
    }, [employers]);

    /** The stage of each employer, computed once per load rather than per render. */
    const stages = useMemo(() => {
        const map = new Map<string, Stage>();
        for (const e of employers) map.set(e.key, stageOf(e));
        return map;
    }, [employers]);

    const stageOfKey = useCallback((e: Employer) => stages.get(e.key) ?? stageOf(e), [stages]);

    /** Follow-ups that are due today or were due before it. The queue. */
    const dueCount = useMemo(
        () => employers.filter(e => stageOfKey(e) === "due").length,
        [employers, stageOfKey]
    );

    /** Everything with a step waiting on us, at any stage of the funnel. */
    const todoCount = useMemo(
        () => employers.filter(e => isTodo(stageOfKey(e))).length,
        [employers, stageOfKey]
    );

    /** The employers I said I would work, still open. */
    const mineCount = useMemo(
        () => employers.filter(e => me && (e.prospect?.owner ?? "").toLowerCase() === me && e.status !== "discarded" && e.status !== "won").length,
        [employers, me]
    );

    /** Raised hands nobody has taken into the list. The most valuable queue here. */
    const untaken = useMemo(() => inbound.filter(isUntaken), [inbound]);

    const visibleInbound = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return inbound;
        return inbound.filter(i =>
            i.employerName.toLowerCase().includes(term)
            || (i.personName ?? "").toLowerCase().includes(term)
            || (i.email ?? "").toLowerCase().includes(term));
    }, [inbound, search]);

    const selectedInbound = useMemo(
        () => visibleInbound.find(i => i.id === selectedInboundId) ?? null,
        [visibleInbound, selectedInboundId]
    );

    /**
     * Take one raised hand into the outreach list and go and stand on it.
     *
     * Reloading and then switching tabs, rather than staying in the inbound
     * queue: the point of the button is that this employer is now an account
     * with a next step, and leaving the operator looking at the message they
     * just filed would hide the thing they just made.
     */
    const take = useCallback(async (item: InboundItem) => {
        if (!data) return;
        setTaking(true);
        try {
            const key = await takeInbound(data, item, prospects.find(p => p.employer_key === item.resolvedKey));
            await load();
            setTab("todo");
            setSelectedKey(key);
            snackbar.open({ type: "success", message: `${item.employerName || key} está en la lista` });
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo pasar a la lista" });
        } finally {
            setTaking(false);
        }
    }, [data, prospects, load, snackbar]);

    /** How many the market filter is holding back from the current tab. */
    const offMarketCount = useMemo(() => {
        const inTab = (e: Employer) => {
            if (tab === "todo") return isTodo(stageOfKey(e));
            if (tab === "due") return stageOfKey(e) === "due";
            if (tab === "mine") return Boolean(me) && (e.prospect?.owner ?? "").toLowerCase() === me;
            return e.status === tab;
        };
        return employers.filter(inTab).filter(e => marketFit(e.countries) === "out" && !isInbound(e)).length;
    }, [employers, tab, stageOfKey, me]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        const inTab = (e: Employer) => {
            if (tab === "todo") return isTodo(stageOfKey(e));
            if (tab === "due") return stageOfKey(e) === "due";
            if (tab === "mine") return Boolean(me) && (e.prospect?.owner ?? "").toLowerCase() === me && e.status !== "discarded" && e.status !== "won";
            return e.status === tab;
        };

        return employers
            .filter(inTab)
            // Somebody who wrote to us is never hidden, wherever they are.
            .filter(e => !marketOnly || isInbound(e) || marketFit(e.countries) !== "out")
            .filter(e => !term || e.name.toLowerCase().includes(term))
            .sort((a, b) => {
                // In the queue the only order that makes sense is by when it was
                // due — a list of overdue work sorted by vacancy count is a list
                // somebody has to re-sort in their head.
                if (tab === "due") {
                    return (a.prospect?.next_action_at ?? "").localeCompare(b.prospect?.next_action_at ?? "");
                }
                // "Por hacer" is sorted by the kind of work, not by the company:
                // the list is meant to be worked top to bottom, and switching
                // between "write an email" and "run a search" every other row is
                // what makes a queue feel like a pile. Inside a batch the usual
                // order applies, and the due rows keep their own by date.
                if (tab === "todo") {
                    const byStage = STAGE_ORDER[stageOfKey(a)] - STAGE_ORDER[stageOfKey(b)];
                    if (byStage !== 0) return byStage;
                    if (stageOfKey(a) === "due") {
                        return (a.prospect?.next_action_at ?? "").localeCompare(b.prospect?.next_action_at ?? "");
                    }
                    // Somebody who wrote to us beats somebody we found, at the
                    // same stage and every time. It is the one ordering rule
                    // here that reflects how much the work is worth rather than
                    // how old it is.
                    const byInbound = (isInbound(b) ? 1 : 0) - (isInbound(a) ? 1 : 0);
                    if (byInbound !== 0) return byInbound;
                }
                // Then the market: an employer in Spain or Latin America ahead
                // of one whose country nobody spelled, ahead of one outside.
                const byMarket = MARKET_ORDER[marketFit(a.countries)] - MARKET_ORDER[marketFit(b.countries)];
                if (byMarket !== 0) return byMarket;
                if (sortBy === "volume") {
                    const byOpen = b.open.length - a.open.length;
                    if (byOpen !== 0) return byOpen;
                }
                // An employer with no live vacancy is a lapsed prospect however
                // recently they last posted, so they sort below every live one
                // under either order.
                const byLive = (b.open.length > 0 ? 1 : 0) - (a.open.length > 0 ? 1 : 0);
                if (byLive !== 0) return byLive;
                return (b.lastSeen ?? "").localeCompare(a.lastSeen ?? "");
            });
    }, [employers, tab, search, sortBy, stageOfKey, marketOnly, me]);

    /** How many rows sit under each heading of the "Por hacer" list. */
    const visibleByStage = useMemo(() => {
        const map = new Map<Stage, number>();
        for (const e of visible) {
            const stage = stageOfKey(e);
            map.set(stage, (map.get(stage) ?? 0) + 1);
        }
        return map;
    }, [visible, stageOfKey]);

    const selected = useMemo(
        () => employers.find(e => e.key === selectedKey) ?? null,
        [employers, selectedKey]
    );

    /**
     * The first tab, decided once the first load is in — and the first
     * selection with it, when a link named one.
     *
     * A link outranks the queue: somebody asked for one employer, and it is
     * often in a tab the screen would not open on — already a client, or
     * outside the market filter. The tab has to be one that holds it and the
     * filter has to let it through, or the effects below move straight off it.
     *
     * Declared after `employers` because it reads them, and before those
     * effects because on the commit where the list lands they still see the
     * tab and the selection from before this decision; both wait for
     * `tabChosen` rather than overwrite it. That race is exactly how Correo's
     * links landed on the top of its queue instead of on their conversation.
     */
    useEffect(() => {
        if (tabChosen || offers === null) return;
        setTabChosen(true);
        const link = pendingDeepLink.current;
        pendingDeepLink.current = null;
        if (link?.inbound && inbound.some(i => i.id === link.inbound)) {
            setTab("inbound");
            setSelectedInboundId(link.inbound);
            openDetail();
            return;
        }
        const linked = link?.employer ? employers.find(e => e.key === link.employer) : undefined;
        if (linked) {
            setTab(isTodo(stageOfKey(linked)) ? "todo" : linked.status);
            if (marketFit(linked.countries) === "out" && !isInbound(linked)) setMarketOnly(false);
            setSelectedKey(linked.key);
            openDetail();
            return;
        }
        if (inbound.some(isUntaken)) setTab("inbound");
    }, [tabChosen, offers, inbound, employers, stageOfKey, openDetail]);

    // Keep the selection on something the current tab contains. Not while the
    // inbound queue is open: that tab has its own selection and clearing this
    // one would throw away the employer the operator was reading. Not before
    // the first tab is chosen, either — see above.
    useEffect(() => {
        if (!tabChosen || tab === "inbound") return;
        if (visible.length === 0) {
            setSelectedKey(null);
            return;
        }
        if (!selectedKey || !visible.some(e => e.key === selectedKey)) {
            setSelectedKey(visible[0].key);
        }
    }, [tabChosen, tab, visible, selectedKey]);

    // The same rule for the inbound queue, and it leads with untaken work: a
    // message already filed is history, and opening on it would show the one
    // row in the list with nothing to do.
    useEffect(() => {
        if (!tabChosen || tab !== "inbound") return;
        if (visibleInbound.length === 0) {
            setSelectedInboundId(null);
            return;
        }
        if (!selectedInboundId || !visibleInbound.some(i => i.id === selectedInboundId)) {
            setSelectedInboundId((visibleInbound.find(isUntaken) ?? visibleInbound[0]).id);
        }
    }, [tabChosen, tab, visibleInbound, selectedInboundId]);

    // Load the draft and the log from whichever employer is now selected.
    useEffect(() => {
        setDraft({
            notes: selected?.prospect?.notes ?? "",
            next_action: selected?.prospect?.next_action ?? "",
            next_action_at: selected?.prospect?.next_action_at ? new Date(selected.prospect.next_action_at) : null
        });
        void loadActivity(selected?.key ?? null, selected?.companyId ?? null);
    }, [selected?.key, selected?.companyId, selected?.prospect?.id, loadActivity]);

    /**
     * Write the outreach record for an employer, creating it on first use.
     *
     * `employer_key` is unique, so this is an upsert in two steps rather than
     * one — the data plane has no upsert, and the local map is authoritative
     * because every row was just loaded. A create that loses the race with
     * another tab fails on the unique constraint, which is the correct outcome:
     * better a visible error than a second row holding a different status for
     * the same company.
     */
    const saveProspect = useCallback(async (employer: Employer, patch: Record<string, unknown>) => {
        if (!data) return;
        setWorking(true);
        try {
            const existing = employer.prospect;
            if (existing) {
                await data.collection("company-prospects").update(existing.id, patch);
                setProspects(rows => rows.map(r => (r.id === existing.id ? { ...r, ...patch } as ProspectRow : r)));
            } else {
                const created = await data.collection("company-prospects").create({
                    employer_key: employer.key,
                    employer_name: employer.name,
                    employer_website: employer.website,
                    ...patch
                });
                const row = (created?.data ?? created) as ProspectRow | undefined;
                if (row?.id && row?.employer_key) {
                    setProspects(rows => [...rows, row]);
                } else {
                    // The write succeeded but the response was not a row we can
                    // merge. Leaving local state alone would show the employer
                    // as untouched, and the next click would try to create a
                    // second row and fail on the unique key — so re-read instead
                    // of guessing what was stored.
                    await load();
                }
            }
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo guardar" });
            // The optimistic state above is only reached on success, so a failed
            // write leaves the screen showing what the database still holds.
            throw err;
        } finally {
            setWorking(false);
        }
    }, [data, snackbar, load]);

    /**
     * Append one entry to an employer's history.
     *
     * Never throws at the caller. The log is a record of an action that already
     * happened — a status was set, an email client opened — and failing the
     * action because its log entry did not write would be the wrong way round.
     */
    const logActivity = useCallback(async (
        employer: Employer,
        kind: ActivityKind,
        body: string,
        contactId?: string | null
    ) => {
        if (!data) return;
        try {
            await data.collection("prospect-activity").create({
                employer_key: employer.key,
                kind,
                body,
                contact_id: contactId ?? null,
                // Stamped by the browser because the data plane does not know
                // what this row means. `find-contacts` stamps its own; an entry
                // with no author is a log that cannot answer "who called them".
                actor: (auth as any)?.user?.email || (auth as any)?.user?.uid || null
            });
            await loadActivity(employer.key, employer.companyId);
        } catch (err) {
            console.error("No se pudo registrar la actividad", err);
        }
    }, [data, loadActivity, auth]);

    const setStatus = useCallback(async (employer: Employer, status: ProspectStatus) => {
        const patch: Record<string, unknown> = { status };
        // Stamped here rather than on every write: the date answers "when did we
        // last reach out", and editing a note weeks later is not reaching out.
        if (status === "contacted") patch.last_contacted_at = new Date().toISOString();
        try {
            await saveProspect(employer, patch);
            await logActivity(employer, "status", `${statusLabel(employer.status)} → ${statusLabel(status)}`);
            snackbar.open({ type: "success", message: `${employer.name} → ${statusLabel(status)}` });
            // Follow the record instead of losing it. Marking an employer
            // "Contactada" from the "Sin contactar" tab drops them out of the
            // filter, and the pane jumps to whoever was next — the operator is
            // then reading someone else's account while still thinking about
            // this one. In the two work queues the opposite is right: moving on
            // *is* the workflow, so those tabs stay put.
            setTab(current => (current === "due" || current === "todo" ? current : status));
        } catch {
            /* reported by saveProspect */
        }
    }, [saveProspect, logActivity, snackbar]);

    const saveContext = useCallback(async (employer: Employer) => {
        try {
            await saveProspect(employer, { notes: draft.notes || null });
            snackbar.open({ type: "success", message: "Guardado" });
        } catch {
            /* reported by saveProspect */
        }
    }, [draft.notes, saveProspect, snackbar]);

    const saveNextAction = useCallback(async (employer: Employer, when: Date | null, what?: string) => {
        const text = (what ?? draft.next_action).trim();
        try {
            await saveProspect(employer, {
                next_action: text || null,
                next_action_at: when ? when.toISOString() : null
            });
            setDraft(d => ({ ...d, next_action: text, next_action_at: when }));
        } catch {
            /* reported by saveProspect */
        }
    }, [draft.next_action, saveProspect]);

    const completeNextAction = useCallback(async (employer: Employer) => {
        const done = employer.prospect?.next_action?.trim();
        try {
            await saveProspect(employer, { next_action: null, next_action_at: null });
            setDraft(d => ({ ...d, next_action: "", next_action_at: null }));
            if (done) await logActivity(employer, "note", `Hecho: ${done}`);
        } catch {
            /* reported by saveProspect */
        }
    }, [saveProspect, logActivity]);

    const liveCount = employers.filter(e => e.open.length > 0).length;

    const selectedStage = selected ? stageOfKey(selected) : null;

    /**
     * Who "Escribir" would open the composer on, and who it would fall back to.
     *
     * `reachable` counts an address *or* a profile, so an employer can be at the
     * "escribir" stage with nobody to send mail to — the button then has to
     * offer the profile instead of doing nothing, which is the honest version of
     * "you can reach these people".
     */
    const writeTarget = useMemo(
        () => (selected ? [...selected.contacts].sort(compareContacts).find(c => (c.email ?? "").trim()) ?? null : null),
        [selected]
    );

    const profileTarget = useMemo(
        () => (selected ? [...selected.contacts].sort(compareContacts).find(c => (c.linkedin_url ?? "").trim()) ?? null : null),
        [selected]
    );

    /** Hand a step to the contacts panel, which owns the search, form and composer. */
    const askPanel = useCallback((action: PanelAsk["action"], contactId?: string) => {
        setAsk(current => ({ action, contactId: contactId ?? null, n: (current?.n ?? 0) + 1 }));
    }, []);

    return (
        <div className={workspaceRootCls}>
            {liveRegion}

            {/* ── Employer list ── */}
            <div className={cls(masterCls, defaultBorderMixin)}>
                <div className="p-4 pb-2 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <Typography variant="subtitle1" component="h1" className="grow">Empresas que contratan</Typography>
                        <Tooltip title="Recargar" asChild>
                            <IconButton size="smallest" aria-label="Volver a mirar las empresas" onClick={load} disabled={loading} aria-busy={loading}>
                                <RefreshCwIcon size={iconSize.smallest} className={cls(loading && "animate-spin")} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/*
                        What this screen is for, in one sentence. It reads as
                        obvious once you know it and it is not obvious at all
                        from a list of company names and six status chips —
                        which is exactly the sentence a workspace has to say
                        before it starts counting things.
                    */}
                    <Typography variant="body2" color="secondary">
                        Empresas que acaban de publicar una vacante de sostenibilidad. Aquí se busca
                        a quién escribir, se le escribe y se programa el seguimiento.
                    </Typography>

                    <Typography variant="body2" color="secondary">
                        {employers.length} empresas · {liveCount} con vacante abierta
                    </Typography>

                    {dropped > 0 && (
                        <Alert color="warning" size="small">
                            Faltan {dropped} ofertas por antigüedad: esta pantalla lee las {OFFER_LIMIT}
                            {" "}más recientes. Los recuentos de vacantes son de esas, no de todas.
                        </Alert>
                    )}

                    <SearchBar
                        size="small"
                        placeholder="Buscar empresa"
                        onTextSearch={(term: string | undefined) => setSearch(term ?? "")}
                    />

                    {/*
                        The work first, the funnel second, and a line between
                        them. These are two different questions — "what do I do"
                        and "where does this company stand" — and putting the
                        eight chips in one row made the first one impossible to
                        find, because six of the eight answered the second.
                    */}
                    <div className="flex flex-wrap gap-1.5">
                        <FilterChip active={tab === "todo"} onClick={() => setTab("todo")}>
                            Por hacer · {todoCount}
                        </FilterChip>
                        <FilterChip active={tab === "due"} onClick={() => setTab("due")}>
                            Para hoy · {dueCount}
                        </FilterChip>
                        <FilterChip active={tab === "mine"} onClick={() => setTab("mine")}>
                            Asignadas a mí · {mineCount}
                        </FilterChip>
                        {/*
                            Shown only when there is something in it, and that is
                            the exception to the rule the other two follow. A
                            permanent "Entrantes · 0" would be a fourth chip on a
                            screen whose whole problem was too many controls;
                            appearing when somebody writes to us is the entire
                            signal.
                        */}
                        {untaken.length > 0 && (
                            <FilterChip active={tab === "inbound"} onClick={() => setTab("inbound")}>
                                Desde la web · {untaken.length}
                            </FilterChip>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Was "Embudo". Nobody outside a sales course calls a
                            list of employers a funnel, and the word says
                            nothing about what the six chips under it do. */}
                        <Typography variant="body2" color="secondary" className="w-full">
                            En qué punto está cada empresa
                        </Typography>
                        {TABS.map(t => (
                            <FilterChip key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                {t.label} · {counts[t.id]}
                            </FilterChip>
                        ))}
                    </div>

                    {/* What the open filter holds, where somebody would ask. */}
                    <Typography variant="body2" color="secondary">
                        {PROSPECT_TAB_HINT[tab] ?? ""}
                    </Typography>

                    {/*
                        Only outside the work queues: "Por hacer" is already
                        ordered by the kind of work and "Para hoy" by the date it
                        came due, and re-sorting either of those by vacancy count
                        would undo the thing that makes them queues.
                    */}
                    {tab !== "inbound" && (offMarketCount > 0 || !marketOnly) && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <FilterChip active={marketOnly} onClick={() => setMarketOnly(v => !v)}>
                                {marketOnly
                                    ? `Sólo España y Latinoamérica · ${offMarketCount} fuera`
                                    : "Todos los países"}
                            </FilterChip>
                        </div>
                    )}
                    {tab !== "todo" && tab !== "due" && tab !== "inbound" && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <Typography variant="body2" color="secondary">Ordenar por</Typography>
                            <FilterChip active={sortBy === "recent"} onClick={() => setSortBy("recent")}>
                                actividad
                            </FilterChip>
                            <FilterChip active={sortBy === "volume"} onClick={() => setSortBy("volume")}>
                                nº de vacantes
                            </FilterChip>
                        </div>
                    )}
                </div>

                <div className={workspaceListCls}>
                    {error && <Alert color="error">{error}</Alert>}
                    {offers === null && !error && <CircularProgressCenter />}

                    {/* ── The raised hands ── */}
                    {tab === "inbound" && visibleInbound.map(item => {
                        const untakenItem = isUntaken(item);
                        return (
                            <Card
                                key={`${item.kind}:${item.id}`}
                                onClick={() => { setSelectedInboundId(item.id); openDetail(); }}
                                className={cls(
                                    "p-3 flex flex-col gap-1",
                                    cardClickableMixin,
                                    item.id === selectedInboundId && cardSelectedMixin,
                                    // A row already taken is history, not work.
                                    !untakenItem && "opacity-60"
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <Typography variant="subtitle2" component="span" className="truncate grow">
                                        {item.employerName || item.personName || item.email || "(sin nombre)"}
                                    </Typography>
                                    <Typography variant="body2" color="secondary" className="shrink-0">
                                        {sinceLabel(item.createdAt)}
                                    </Typography>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Chip size="small" colorScheme={INBOUND_COLOR[item.kind]}>
                                        {INBOUND_LABEL[item.kind]}
                                    </Chip>
                                    {!untakenItem && (
                                        <Chip size="small" colorScheme="grayLighter">ya está en la lista</Chip>
                                    )}
                                    {untakenItem && !item.resolvedKey && (
                                        <Chip size="small" colorScheme="orangeLighter">sin empresa clara</Chip>
                                    )}
                                </div>
                                <Typography variant="body2" color="secondary" className="truncate">
                                    {item.body}
                                </Typography>
                            </Card>
                        );
                    })}
                    {tab === "inbound" && visibleInbound.length === 0 && (
                        <div className="flex items-center justify-center py-8 px-4 text-center">
                            <Typography variant="label" color="secondary">Nadie ha escrito todavía</Typography>
                        </div>
                    )}

                    {tab !== "inbound" && offers !== null && visible.length === 0 && !error && (
                        <div className="flex items-center justify-center py-8 px-4 text-center">
                            <Typography variant="label" color="secondary">
                                {tab === "todo"
                                    ? "No queda nada por hacer aquí"
                                    : tab === "due" ? "Nada pendiente para hoy" : "Nada aquí"}
                            </Typography>
                        </div>
                    )}
                    {tab !== "inbound" && visible.map((e, index) => {
                        const due = dueState(e.prospect?.next_action_at);
                        const stage = stageOfKey(e);
                        // "Por hacer" is read as batches: five headings over
                        // thirty rows say more than thirty identical chips, and
                        // they let somebody decide to spend the morning writing
                        // rather than one row at a time.
                        const grouped = tab === "todo";
                        const heads = grouped && (index === 0 || stageOfKey(visible[index - 1]) !== stage);

                        return (
                            <React.Fragment key={e.key}>
                                {heads && (
                                    <div className="flex items-baseline gap-2 px-2 pt-4 pb-1 first:pt-1">
                                        <Typography variant="micro">
                                            {STAGE_LABEL[stage]}
                                        </Typography>
                                        <Typography variant="body2" color="secondary">
                                            {visibleByStage.get(stage) ?? 0}
                                        </Typography>
                                    </div>
                                )}
                                <Card
                                    onClick={() => { setSelectedKey(e.key); openDetail(); }}
                                    className={cls(
                                        "p-3 flex flex-col gap-1",
                                        cardClickableMixin,
                                        e.key === selectedKey && cardSelectedMixin
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <Typography variant="subtitle2" component="span" className="truncate grow">{e.name}</Typography>
                                        {e.prospect?.owner && (
                                            <Chip size="small" colorScheme="grayLighter">{shortName(e.prospect.owner)}</Chip>
                                        )}
                                        {due !== "none" && due !== "later" && (
                                            <Chip size="small" colorScheme={DUE_COLOR[due]}>
                                                {dueLabel(e.prospect?.next_action_at)}
                                            </Chip>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* Under a heading the chip would repeat it. */}
                                        {!grouped && stage !== "closed" && (
                                            <Chip size="small" colorScheme={STAGE_COLOR[stage]}>
                                                {STAGE_LABEL[stage]}
                                            </Chip>
                                        )}
                                        {/* Worth its own chip and not a footnote:
                                            it changes what the first sentence of
                                            the email has to be. */}
                                        {isInbound(e) && (
                                            <Chip size="small" colorScheme="greenLighter">desde la web</Chip>
                                        )}
                                        {/* An employer who came to us may have no
                                            scraped vacancy at all, and "0 vacantes
                                            abiertas" would read as a lapsed
                                            prospect rather than a new one. */}
                                        {!(isInbound(e) && e.open.length === 0 && e.past.length === 0) && (
                                            <Chip size="small" colorScheme={e.open.length > 0 ? "greenLighter" : "grayLighter"}>
                                                {e.open.length === 1 ? "1 vacante abierta" : `${e.open.length} vacantes abiertas`}
                                            </Chip>
                                        )}
                                        {e.past.length > 0 && (
                                            <Typography variant="body2" color="secondary">
                                                +{e.past.length} caducada{e.past.length === 1 ? "" : "s"}
                                            </Typography>
                                        )}
                                    </div>
                                    <Typography variant="body2" color="secondary" className="truncate">
                                        {(tab === "due" || stage === "due") && e.prospect?.next_action
                                            ? e.prospect.next_action
                                            : <>
                                                {e.reachable > 0 && `${e.reachable} a quien escribir · `}
                                                {sinceLabel(e.lastSeen)}
                                                {e.countries.length > 0 && ` · ${e.countries.join(", ")}`}
                                            </>}
                                    </Typography>
                                </Card>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* ── Detail ── */}
            <div className={detailCls}>
                <BackToList label="Empresas" onClick={closeDetail}/>
                {tab === "inbound" && !selectedInbound && (
                    <div className="flex items-center justify-center h-full">
                        <Typography variant="label" color="secondary">Elige un mensaje</Typography>
                    </div>
                )}

                {tab === "inbound" && selectedInbound && (
                    <div className="p-6 max-w-3xl flex flex-col gap-5">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3 flex-wrap">
                                <Typography variant="h5" component="h2">
                                    {selectedInbound.companyId
                                        ? <EntityLink kind="company" id={selectedInbound.companyId}>{selectedInbound.employerName || "(sin nombre)"}</EntityLink>
                                        : (selectedInbound.employerName || selectedInbound.personName || "(sin nombre)")}
                                </Typography>
                                <Chip size="small" colorScheme={INBOUND_COLOR[selectedInbound.kind]}>
                                    {INBOUND_LABEL[selectedInbound.kind]}
                                </Chip>
                            </div>
                            {/* Who wrote, as a person you can reach: name, role,
                                address, phone. This pane used to show a company
                                name and a checkbox and nothing about who. */}
                            <Typography variant="body2">
                                {selectedInbound.personName
                                    ? <span className="font-medium">{selectedInbound.personName}</span>
                                    : <span className="opacity-70">Sin nombre de contacto</span>}
                                {selectedInbound.personPosition && <span className="opacity-70"> · {selectedInbound.personPosition}</span>}
                                <span className="opacity-70"> · {sinceLabel(selectedInbound.createdAt)}</span>
                            </Typography>
                            <Typography variant="body2" className="flex flex-wrap gap-x-3">
                                {selectedInbound.email && (
                                    <a href={`mailto:${selectedInbound.email}`} className="text-primary hover:underline">{selectedInbound.email}</a>
                                )}
                                {selectedInbound.personPhone && (
                                    <a href={`tel:${selectedInbound.personPhone}`} className="text-primary hover:underline">{selectedInbound.personPhone}</a>
                                )}
                                {selectedInbound.website && (
                                    <a href={selectedInbound.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                        {selectedInbound.website.replace(/^https?:\/\//, "")}
                                    </a>
                                )}
                            </Typography>
                        </div>

                        {/* What they said. Honest when the form carried no
                            words: "Pidió: selección y contratación." was a
                            checkbox dressed as a message. */}
                        <Card className={cls("p-4 flex flex-col gap-1", defaultBorderMixin)}>
                            <Typography variant="micro" color="secondary">
                                {selectedInbound.kind === "lead"
                                    ? "Registro de empresa en sustentalent.com"
                                    : "Formulario de contacto de sustentalent.com"}
                            </Typography>
                            <Typography variant="body1" className="whitespace-pre-wrap break-words">
                                {selectedInbound.body}
                            </Typography>
                            {selectedInbound.kind === "lead" && !/[.!?]\s+\S/.test(selectedInbound.body) && (
                                <Typography variant="body2" color="secondary">
                                    Esto viene del formulario de registro de empresas de la web, que no pide un mensaje: son las casillas que marcó. Lo que haya dicho por correo está abajo.
                                </Typography>
                            )}
                        </Card>

                        {/*
                            The one action. An inbound row was previously a
                            read-only cell in a table with no next step, which is
                            why five of five leads were still sitting at "new" —
                            not because nobody cared, but because there was
                            nowhere for the caring to go.
                        */}
                        {isUntaken(selectedInbound) && selectedInbound.resolvedKey && (
                            <div className="flex flex-col gap-2">
                                <Button size="small" disabled={taking} onClick={() => take(selectedInbound)}>
                                    Pasar a la lista y trabajarla
                                </Button>
                                <Typography variant="body2" color="secondary">
                                    Crea la ficha de {selectedInbound.employerName || selectedInbound.resolvedKey} como
                                    «desde la web», guarda lo que marcaron en su historial
                                    {selectedInbound.email ? " y añade a quien escribió como persona de contacto" : ""}.
                                </Typography>
                            </div>
                        )}

                        {isUntaken(selectedInbound) && !selectedInbound.resolvedKey && (
                            <Alert color="warning" size="small">
                                No se puede deducir la empresa de esta dirección — escribieron desde un correo
                                personal, o no dejaron ninguno. Si resulta que sí es una empresa, créala desde
                                «Correo» cuando contesten, o añádela a mano.
                            </Alert>
                        )}

                        {!isUntaken(selectedInbound) && (
                            <Alert color="info" size="small">
                                Ya está en la lista. Su ficha, su historial y su próximo paso están
                                en «Por hacer».
                            </Alert>
                        )}

                        {/* The conversation with them, and the reply, right
                            here. A screen that says "they wrote to us" and
                            sends you elsewhere to read what they wrote is
                            not a CRM. */}
                        {(selectedInbound.companyId || selectedInbound.resolvedKey) && (
                            <>
                                <Separator />
                                <EntityEmailPanel
                                    kind="company"
                                    entityId={selectedInbound.companyId ?? ""}
                                    email={selectedInbound.email}
                                    displayName={selectedInbound.employerName || null}
                                    employerKey={selectedInbound.resolvedKey}
                                    recipientName={selectedInbound.personName}
                                />
                            </>
                        )}
                    </div>
                )}

                {tab !== "inbound" && !selected && (
                    <div className="flex items-center justify-center h-full">
                        <Typography variant="label" color="secondary">
                            Elige una empresa
                        </Typography>
                    </div>
                )}

                {tab !== "inbound" && selected && (
                    <div className="p-6 max-w-3xl flex flex-col gap-6">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3 flex-wrap">
                                <Typography variant="h5" component="h2">
                                    {selected.companyId
                                        ? <EntityLink kind="company" id={selected.companyId}>{selected.name}</EntityLink>
                                        : selected.name}
                                </Typography>
                                <Chip size="small" colorScheme={STATUS_COLOR[selected.status]}>
                                    {statusLabel(selected.status)}
                                </Chip>
                            </div>
                            <div className="flex items-center gap-4 flex-wrap">
                                {selected.website && (
                                    <a
                                        href={selected.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                                    >
                                        <GlobeIcon size={iconSize.smallest} />
                                        {selected.website.replace(/^https?:\/\//, "")}
                                    </a>
                                )}
                                <Typography variant="body2" color="secondary">
                                    Última vacante {sinceLabel(selected.lastSeen)}
                                    {selected.countries.length > 0 && ` · ${selected.countries.join(", ")}`}
                                </Typography>
                                {selected.prospect?.last_contacted_at && (
                                    <Typography variant="body2" color="secondary">
                                        Contactada {sinceLabel(selected.prospect.last_contacted_at)}
                                    </Typography>
                                )}
                            </div>
                            {/* Who is working this employer. Two people writing
                                to the same company on the same morning with
                                nothing warning either is the gap PRODUCT.md
                                names first; this is the string that closes it. */}
                            <OwnerControl
                                owner={selected.prospect?.owner}
                                disabled={working}
                                onChange={next => saveProspect(selected, { owner: next })}
                            />
                        </div>

                        {/*
                            ── Lo siguiente ──

                            The whole point of the screen, and until now the one
                            thing it did not say. Everything below this card is a
                            control somebody has to know they need; this is the
                            sentence that says which one, why, and does it. It is
                            deliberately a single button: an employer has exactly
                            one next step, and offering three is how a to-do list
                            turns back into a control panel.
                        */}
                        {selectedStage && selectedStage !== "closed" && (
                            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Typography variant="micro" color="secondary">
                                        Lo siguiente
                                    </Typography>
                                    <Chip size="small" colorScheme={STAGE_COLOR[selectedStage]}>
                                        {STAGE_LABEL[selectedStage]}
                                    </Chip>
                                </div>

                                <Typography variant="body2">{stageHint(selected)}</Typography>

                                <div className="flex gap-2 flex-wrap items-center">
                                    {selectedStage === "due" && (
                                        <Button size="small" disabled={working}
                                            onClick={() => completeNextAction(selected)}>
                                            {STAGE_ACTION.due}
                                        </Button>
                                    )}

                                    {selectedStage === "find" && (
                                        <Button size="small" disabled={working} onClick={() => askPanel("find")}>
                                            {STAGE_ACTION.find}
                                        </Button>
                                    )}

                                    {selectedStage === "reach" && (
                                        <>
                                            <Button size="small" disabled={working} onClick={() => askPanel("add")}>
                                                {STAGE_ACTION.reach}
                                            </Button>
                                            <Button size="small" variant="outlined" disabled={working}
                                                onClick={() => askPanel("find")}>
                                                Buscar de nuevo
                                            </Button>
                                        </>
                                    )}

                                    {selectedStage === "write" && writeTarget && (
                                        <Button size="small" disabled={working}
                                            onClick={() => askPanel("write", writeTarget.id)}>
                                            {STAGE_ACTION.write} a {contactLabel(writeTarget)}
                                        </Button>
                                    )}

                                    {/* Nobody with an address, but somebody with a profile. */}
                                    {selectedStage === "write" && !writeTarget && profileTarget && (
                                        <a
                                            href={profileTarget.linkedin_url as string}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                                        >
                                            Abrir el LinkedIn de {contactLabel(profileTarget)}
                                            <ExternalLinkIcon size={12} />
                                        </a>
                                    )}

                                    {selectedStage === "chase" && (
                                        <Button size="small" disabled={working}
                                            onClick={() => saveNextAction(
                                                selected,
                                                inDays(3),
                                                draft.next_action || "Hacer seguimiento"
                                            )}>
                                            {STAGE_ACTION.chase}
                                        </Button>
                                    )}

                                    {/*
                                        The way out. Thirty-odd employers arrive
                                        from a scrape nobody chose, some of them
                                        are not worth an hour, and without this
                                        the only way to stop being asked about
                                        one is to leave it in the queue forever.
                                    */}
                                    {selectedStage !== "waiting" && (
                                        <Button size="small" variant="text" disabled={working}
                                            onClick={() => setStatus(selected, "discarded")}>
                                            No nos interesa
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* ── The people ── */}
                        <ProspectContactsPanel
                            employer={selected}
                            client={client}
                            ask={ask}
                            onChanged={load}
                            onLog={(kind, body, contactId) => logActivity(selected, kind, body, contactId)}
                        />

                        <Separator />

                        {/* ── What they answered ──
                            The threads with this employer and their messages,
                            here, where "did they reply" is asked. The compose
                            button is off: each person above has their own. */}
                        <EntityEmailPanel
                            kind="company"
                            entityId={selected.companyId ?? ""}
                            email={null}
                            displayName={selected.name}
                            employerKey={selected.key}
                            showCompose={false}
                        />

                        <Separator />

                        {/* ── The follow-up ── */}
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Typography variant="subtitle2" component="h3" className="grow">Próximo paso</Typography>
                                {selected.prospect?.next_action_at && (
                                    <Chip size="small" colorScheme={DUE_COLOR[dueState(selected.prospect.next_action_at)]}>
                                        {dueLabel(selected.prospect.next_action_at)}
                                    </Chip>
                                )}
                            </div>
                            <div className="flex gap-3 flex-wrap items-end">
                                <TextField
                                    size="small"
                                    label="Qué toca hacer"
                                    value={draft.next_action}
                                    onChange={(e: any) => setDraft(d => ({ ...d, next_action: e.target.value }))}
                                    className="grow"
                                />
                                <DateTimeField
                                    size="small"
                                    mode="date"
                                    label="Cuándo"
                                    clearable
                                    value={draft.next_action_at}
                                    onChange={(date: Date | null) => setDraft(d => ({ ...d, next_action_at: date }))}
                                />
                            </div>
                            <div className="flex gap-2 flex-wrap items-center">
                                <Button size="small" disabled={working}
                                    onClick={() => saveNextAction(selected, draft.next_action_at)}>
                                    Guardar
                                </Button>
                                {/*
                                    The three delays anybody actually uses. Typing
                                    a date for "call them back next week" is the
                                    kind of friction that ends with nobody setting
                                    a follow-up at all.
                                */}
                                {[["+3 días", 3], ["+1 semana", 7], ["+1 mes", 30]].map(([label, days]) => (
                                    <Button key={label as string} size="small" variant="text" disabled={working}
                                        onClick={() => saveNextAction(selected, inDays(days as number))}>
                                        {label}
                                    </Button>
                                ))}
                                {selected.prospect?.next_action_at && (
                                    <Button size="small" variant="text" disabled={working}
                                        onClick={() => completeNextAction(selected)}>
                                        Hecho
                                    </Button>
                                )}
                            </div>
                        </div>

                        <Separator />

                        {/* ── The history ── */}
                        <ProspectActivityPanel
                            entries={activity}
                            threads={threads}
                            contacts={selected.contacts}
                            busy={working}
                            onAdd={async (kind, body) => {
                                await logActivity(selected, kind, body);
                                // A logged call or email is a contact attempt, so
                                // the "last contacted" stamp follows it — without
                                // this the date only ever moved on a status
                                // change, and stopped being true after the first.
                                if (kind === "email" || kind === "call" || kind === "meeting") {
                                    try {
                                        await saveProspect(selected, { last_contacted_at: new Date().toISOString() });
                                    } catch {
                                        /* reported by saveProspect */
                                    }
                                }
                            }}
                            // A reply sent from the conversation panel is a
                            // new line on this timeline.
                            onConversationChanged={() => void loadActivity(selected.key, selected.companyId)}
                        />

                        <Separator />

                        {/*
                            ── The funnel ──

                            Demoted from the top of the pane, where six unlabelled
                            buttons were the first thing anybody saw and read as
                            the thing to do. It is not: the status is a record of
                            where the conversation got to, and it mostly follows
                            the work rather than starting it — sending the first
                            email moves it on its own. Kept, labelled, and out of
                            the way of the step that matters.
                        */}
                        <div className="flex flex-col gap-3">
                            <Typography variant="subtitle2" component="h3">Estado</Typography>
                            <div className="flex flex-wrap gap-2">
                                {TABS.map(t => (
                                    <Button
                                        key={t.id}
                                        size="small"
                                        variant={selected.status === t.id ? "filled" : "outlined"}
                                        disabled={working || selected.status === t.id}
                                        onClick={() => setStatus(selected, t.id)}
                                    >
                                        {t.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* ── Standing context ── */}
                        <div className="flex flex-col gap-3">
                            <Typography variant="subtitle2" component="h3">Contexto</Typography>
                            <Typography variant="body2" color="secondary">
                                Lo que conviene saber siempre de esta empresa. Lo que pasó cada día va en Actividad.
                            </Typography>
                            <TextField
                                size="small"
                                label="Notas"
                                multiline
                                rows={4}
                                value={draft.notes}
                                onChange={(e: any) => setDraft(d => ({ ...d, notes: e.target.value }))}
                            />
                            <div>
                                <Button size="small" disabled={working} onClick={() => saveContext(selected)}>
                                    Guardar
                                </Button>
                            </div>
                        </div>

                        <Separator />

                        {/* ── The offers that put them on this list ── */}
                        <div className="flex flex-col gap-3">
                            <Typography variant="subtitle2" component="h3">
                                Sus vacantes ({selected.open.length + selected.past.length})
                            </Typography>
                            {[...selected.open, ...selected.past].map(o => (
                                <Card key={o.id} className="p-3 flex flex-col gap-1">
                                    <div className="flex items-start gap-2">
                                        <Typography variant="body2" className="grow font-medium">
                                            {o.title}
                                        </Typography>
                                        {o.status === "expired" && (
                                            <Chip size="small" colorScheme="grayLighter">caducada</Chip>
                                        )}
                                    </div>
                                    <Typography variant="body2" color="secondary">
                                        {[o.location_city, o.location_country].filter(Boolean).join(", ") || o.location || "—"}
                                        {" · "}{sinceLabel(offerDate(o))}
                                    </Typography>
                                    <div className="flex items-center gap-4 pt-1">
                                        {/* Into the review queue, at this offer — the same deep
                                            link the scrape cron posts to Discord. */}
                                        <a
                                            href={`/ofertas-por-revisar?oferta=${encodeURIComponent(o.id)}`}
                                            className="text-xs text-primary hover:underline"
                                        >
                                            ver en la cola
                                        </a>
                                        {/* `offerLink`, not `source_url`. For 112 of
                                            113 scraped offers `source_url` is a
                                            Google Jobs search page, so this link
                                            went to Google essentially always;
                                            the real posting is in
                                            `application_link`. See
                                            config/offer_link.ts. */}
                                        {offerLink(o) && (
                                            <a
                                                href={offerLink(o) as string}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                oferta original
                                                <ExternalLinkIcon size={12} />
                                            </a>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
